/**
 * Generated by orval v7.17.2 🍺
 * Do not edit manually.
 * Affiliate Postback Receiver API
 * Production-ready S2S postback ingestion and querying service for affiliate marketing platforms.

## Features
- **Postback Ingestion**: Accept affiliate conversion events via GET/POST webhooks
- **Deduplication**: Automatic duplicate detection to prevent double counting
- **Event Normalization**: Standardize events from multiple affiliate networks
- **Query API**: Search and filter historical events with pagination
- **Analytics**: Statistics aggregations and CSV export for reporting
- **Health Checks**: Kubernetes-ready liveness and readiness probes
- **Metrics**: Prometheus-compatible metrics for monitoring

## Supported Networks
- Referon (via `/postbacks/referon` endpoint)

## Event Types
- **CLICK**: User clicked on affiliate link
- **REG**: User registration
- **FTD**: First-time deposit
- **DEP**: Regular deposit
- **REDEP**: Repeat deposit
 * OpenAPI spec version: 1.0.0
 */
import { useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type { BrandsControllerGetBrandsParams, BrandsResponseDto } from '.././model';

import { customFetch } from '../../../shared/api/custom-client';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * Retrieve list of unique brands with event counts and sources
 * @summary List all brands
 */
export type brandsControllerGetBrandsResponse200 = {
  data: BrandsResponseDto;
  status: 200;
};

export type brandsControllerGetBrandsResponseSuccess = brandsControllerGetBrandsResponse200 & {
  headers: Headers;
};
export type brandsControllerGetBrandsResponse = brandsControllerGetBrandsResponseSuccess;

export const getBrandsControllerGetBrandsUrl = (params?: BrandsControllerGetBrandsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/brands?${stringifiedParams}` : `/brands`;
};

export const brandsControllerGetBrands = async (
  params?: BrandsControllerGetBrandsParams,
  options?: RequestInit,
): Promise<brandsControllerGetBrandsResponse> => {
  return customFetch<brandsControllerGetBrandsResponse>(getBrandsControllerGetBrandsUrl(params), {
    ...options,
    method: 'GET',
  });
};

export const getBrandsControllerGetBrandsQueryKey = (params?: BrandsControllerGetBrandsParams) => {
  return [`/brands`, ...(params ? [params] : [])] as const;
};

export const getBrandsControllerGetBrandsQueryOptions = <
  TData = Awaited<ReturnType<typeof brandsControllerGetBrands>>,
  TError = unknown,
>(
  params?: BrandsControllerGetBrandsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof brandsControllerGetBrands>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBrandsControllerGetBrandsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof brandsControllerGetBrands>>> = ({
    signal,
  }) => brandsControllerGetBrands(params, { signal, ...requestOptions });

  return { queryKey, queryFn, staleTime: 30000, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof brandsControllerGetBrands>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BrandsControllerGetBrandsQueryResult = NonNullable<
  Awaited<ReturnType<typeof brandsControllerGetBrands>>
>;
export type BrandsControllerGetBrandsQueryError = unknown;

export function useBrandsControllerGetBrands<
  TData = Awaited<ReturnType<typeof brandsControllerGetBrands>>,
  TError = unknown,
>(
  params: undefined | BrandsControllerGetBrandsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof brandsControllerGetBrands>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof brandsControllerGetBrands>>,
          TError,
          Awaited<ReturnType<typeof brandsControllerGetBrands>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBrandsControllerGetBrands<
  TData = Awaited<ReturnType<typeof brandsControllerGetBrands>>,
  TError = unknown,
>(
  params?: BrandsControllerGetBrandsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof brandsControllerGetBrands>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof brandsControllerGetBrands>>,
          TError,
          Awaited<ReturnType<typeof brandsControllerGetBrands>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBrandsControllerGetBrands<
  TData = Awaited<ReturnType<typeof brandsControllerGetBrands>>,
  TError = unknown,
>(
  params?: BrandsControllerGetBrandsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof brandsControllerGetBrands>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List all brands
 */

export function useBrandsControllerGetBrands<
  TData = Awaited<ReturnType<typeof brandsControllerGetBrands>>,
  TError = unknown,
>(
  params?: BrandsControllerGetBrandsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof brandsControllerGetBrands>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBrandsControllerGetBrandsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
